import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css"; // We'll create this file

interface WaitlistEntry {
  id: string;
  full_name: string;
  email: string;
  company: string;
  telephone: string | null;
  contact_consent: boolean | null;
  privacy_policy_accepted: boolean | null;
  logo_consent: boolean | null;
  created_at: string;
  survey_responses: {
    questions: { question: string };
    answer: string;
  }[];
}

interface DemoRequest {
  id: string;
  full_name: string;
  email: string;
  company: string;
  phone_number: string;
  message: string;
  created_at: string;
  status: string;
}

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<'waitlist' | 'demos'>('waitlist');
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [demoRequests, setDemoRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<WaitlistEntry | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === 'waitlist') {
      fetchWaitlist();
    } else {
      fetchDemoRequests();
    }
  }, [activeTab]);

  const fetchWaitlist = async () => {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select(`
          *,
          survey_responses (
            questions (question),
            answer
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      console.log("Fetched waitlist data:", data); // Debug log
      setEntries(data as WaitlistEntry[] || []);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDemoRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('demo_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDemoRequests(data || []);
    } catch (error) {
      console.error('Error fetching demo requests:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper functions for displaying values safely
  const displayStringValue = (value: string | null | undefined): string => {
    return value || "Not provided";
  };

  const displayBooleanValue = (value: boolean | null | undefined): string => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "Not specified";
  };

  if (loading) return <div className="min-h-screen bg-white text-black p-8" data-theme="light">Loading...</div>;

  return (
    <div className="min-h-screen bg-white text-black p-8" data-theme="light">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Admin Dashboard</h1>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <span>Logout</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <div className="border-b">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('waitlist')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'waitlist'
                    ? 'border-[#0069ff] text-[#0069ff]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Waitlist Entries
              </button>
              <button
                onClick={() => setActiveTab('demos')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'demos'
                    ? 'border-[#0069ff] text-[#0069ff]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Demo Requests
              </button>
            </nav>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border force-black-text">
          {activeTab === 'waitlist' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 divide-x force-black-text">
              <div className="p-6 force-black-text">
                <div className="overflow-x-auto force-black-text">
                  <table className="w-full text-black force-black-text" cellPadding="0" cellSpacing="0">
                    <thead>
                      <tr className="border-b black-text-row">
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Name</th>
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Email</th>
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Company</th>
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Phone</th>
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Contact</th>
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Privacy</th>
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Logo Consent</th>
                        <th className="text-left py-3 px-4 font-semibold force-black-text">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr 
                          key={entry.id} 
                          className="border-b cursor-pointer hover:bg-gray-50 black-text-row"
                          onClick={() => setSelectedEntry(entry)}
                        >
                          <td className="py-3 px-4 force-black-text">{displayStringValue(entry.full_name)}</td>
                          <td className="py-3 px-4 force-black-text">{displayStringValue(entry.email)}</td>
                          <td className="py-3 px-4 force-black-text">{displayStringValue(entry.company)}</td>
                          <td className="py-3 px-4 force-black-text">{displayStringValue(entry.telephone)}</td>
                          <td className="py-3 px-4 force-black-text">{displayBooleanValue(entry.contact_consent)}</td>
                          <td className="py-3 px-4 force-black-text">{displayBooleanValue(entry.privacy_policy_accepted)}</td>
                          <td className="py-3 px-4 force-black-text">{displayBooleanValue(entry.logo_consent)}</td>
                          <td className="py-3 px-4 force-black-text">
                            {new Date(entry.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="p-6">
                {selectedEntry ? (
                  <div className="text-black">
                    <h2 className="text-xl font-semibold mb-4 text-black">User Details</h2>
                    <div className="space-y-3 mb-6">
                      <div className="grid grid-cols-2 gap-4">
                        <p className="font-medium text-black">Contact Consent:</p>
                        <p className="text-black">{displayBooleanValue(selectedEntry.contact_consent)}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <p className="font-medium text-black">Privacy Policy Accepted:</p>
                        <p className="text-black">{displayBooleanValue(selectedEntry.privacy_policy_accepted)}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <p className="font-medium text-black">Logo Display Consent:</p>
                        <p className="text-black">{displayBooleanValue(selectedEntry.logo_consent)}</p>
                      </div>
                    </div>
                    
                    <h2 className="text-xl font-semibold mb-4 text-black">Survey Responses</h2>
                    <h3 className="font-medium text-black mb-4">{selectedEntry.full_name}</h3>
                    <div className="space-y-4">
                      {selectedEntry.survey_responses.map((response, index) => (
                        <div key={index} className="border-b pb-3">
                          <p className="font-medium text-sm text-black">{response.questions.question}</p>
                          <p className="mt-1 text-black">{response.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    Select an entry to view responses
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full" style={{ color: 'black' }}>
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: 'black' }}>Name</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: 'black' }}>Email</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: 'black' }}>Company</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: 'black' }}>Phone</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: 'black' }}>Message</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: 'black' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demoRequests.map((request) => (
                      <tr key={request.id} className="border-b hover:bg-gray-50" style={{ color: 'black' }}>
                        <td className="py-3 px-4" style={{ color: 'black' }}>{request.full_name}</td>
                        <td className="py-3 px-4" style={{ color: 'black' }}>{request.email}</td>
                        <td className="py-3 px-4" style={{ color: 'black' }}>{request.company}</td>
                        <td className="py-3 px-4" style={{ color: 'black' }}>{request.phone_number}</td>
                        <td className="py-3 px-4" style={{ color: 'black' }}>
                          <div className="max-w-xs truncate">{request.message}</div>
                        </td>
                        <td className="py-3 px-4" style={{ color: 'black' }}>
                          {new Date(request.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}